import React, { useContext, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import {
  Alert,
  Box,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import ConfirmDialog from "components/common/ConfirmDialog";
import { LocationContext } from "context/Location";

const ROLE_OPTIONS = [
  { value: "careverify_manager", label: "CareVerify Manager" },
  { value: "hours_manager", label: "Hours Manager" },
  { value: "capabilities_manager", label: "Capabilities Manager" },
  { value: "visitstart_admin_governor", label: "VisitStart Admin Governor" },
  { value: "visitstart_registration_admin", label: "Registration (Admin)" },
  { value: "visitstart_registration_clinical", label: "Registration (Clinical)" },
];

/**
 * Facility Settings section: assign/revoke CareVerify facilityRoles (PROMPT 2).
 * Server enforces owner/admin only (locations.ownerId | claimedBy | adminUids).
 */
const CareVerifyFacilityRolesSection = () => {
  const { location } = useContext(LocationContext);
  const facilityId = location?.id;
  const facilityRoles = location?.facilityRoles || {};

  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { uid: "", roleType: "careverify_manager" },
  });

  const roleRows = useMemo(() => {
    const rows = [];
    for (const opt of ROLE_OPTIONS) {
      const holders = Array.isArray(facilityRoles[opt.value])
        ? facilityRoles[opt.value]
        : [];
      for (const uid of holders) {
        rows.push({ uid, roleType: opt.value, label: opt.label });
      }
    }
    return rows;
  }, [facilityRoles]);

  if (!facilityId) return null;

  const onAssign = handleSubmit(async (values) => {
    setError("");
    setSuccess("");
    setAssigning(true);
    try {
      const fn = firebase.functions().httpsCallable("assignFacilityRole");
      await fn({
        facilityId,
        uid: values.uid.trim(),
        roleType: values.roleType,
      });
      setSuccess("Role assigned.");
      reset({ uid: "", roleType: values.roleType });
    } catch (err) {
      setError(err?.message || "Could not assign role.");
    } finally {
      setAssigning(false);
    }
  });

  const onConfirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    setError("");
    setSuccess("");
    try {
      const fn = firebase.functions().httpsCallable("revokeFacilityRole");
      await fn({
        facilityId,
        uid: revokeTarget.uid,
        roleType: revokeTarget.roleType,
      });
      setSuccess("Role revoked.");
      setRevokeTarget(null);
    } catch (err) {
      setError(err?.message || "Could not revoke role.");
    } finally {
      setRevoking(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Facility roles
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Annotate facility members with CareVerify or VisitStart desk grants.
        Only a facility owner or admin may assign or revoke roles; the target
        user must already be in this facility&apos;s member list.
      </Typography>

      {success ? (
        <Alert severity="success" onClose={() => setSuccess("")} sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Stack
        component="form"
        onSubmit={onAssign}
        spacing={2}
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "flex-start" }}
        sx={{ mb: 2 }}
      >
        <Controller
          name="uid"
          control={control}
          rules={{ required: "User id is required" }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Member user id"
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="roleType"
          control={control}
          render={({ field }) => (
            <TextField {...field} select label="Role" sx={{ minWidth: 220 }}>
              {ROLE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <LoadingButton type="submit" loading={assigning} sx={{ mt: { sm: 0.5 } }}>
          Assign
        </LoadingButton>
      </Stack>

      {roleRows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No CareVerify roles assigned yet.
        </Typography>
      ) : (
        <List dense>
          {roleRows.map((row) => (
            <ListItem
              key={`${row.roleType}:${row.uid}`}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="Revoke role"
                  onClick={() => setRevokeTarget(row)}
                >
                  <DeleteOutlineRounded />
                </IconButton>
              }
            >
              <ListItemText primary={row.label} secondary={row.uid} />
            </ListItem>
          ))}
        </List>
      )}

      <ConfirmDialog
        open={Boolean(revokeTarget)}
        onClose={() => !revoking && setRevokeTarget(null)}
        title="Revoke facility role?"
        message={
          revokeTarget
            ? `Remove ${revokeTarget.label} from ${revokeTarget.uid}?`
            : ""
        }
        confirmLabel="Revoke"
        confirmColor="error"
        loading={revoking}
        onConfirm={onConfirmRevoke}
      />
    </Box>
  );
};

export default CareVerifyFacilityRolesSection;
